import { useEffect, useRef, useState } from "react";
import { avisarError } from "../../shared/errores";
import { eliminarProyecto } from "./proyectosService";
import { Avatar, Barra, Etiqueta } from "./componentes";
import Documentos from "./Documentos";
import { botonFantasma, botonPeligro, botonPrimario, contenedor, etiqueta, filaResaltable, panel, pestana, tituloPagina } from "./estilos";
import FormProyecto from "./FormProyecto";
import FormTarea from "./FormTarea";
import { confirmar } from "../../shared/avisos";
import Selector from "../../shared/components/Selector";
import {
  ESTADOS_PROYECTO, ESTADOS_TAREA, PRIORIDADES, SALUD, avance, avanceEsperado, datosDe, esAdministrador,
  esResponsable, estaHecha, estaVencida, fechaCorta, gestiona, nombrePersona, ordenarTareas, salud, vencimiento,
} from "./reglas";

const VISTAS = [
  { id: "tablero", label: "Tablero" },
  { id: "lista", label: "Lista" },
  { id: "documentos", label: "Documentos" },
];

const VISTA_GUARDADA = "proyectos-vista";
const leerVista = () => {
  try { return localStorage.getItem(VISTA_GUARDADA) || "tablero"; } catch { return "tablero"; }
};

function ContenidoTarjeta({ tarea }) {
  const vence = vencimiento(tarea);
  const prioridad = datosDe(PRIORIDADES, tarea.prioridad);
  const responsable = nombrePersona(tarea.responsable);
  return (
    <>
      <div className="mb-2 flex items-start justify-between gap-2">
        <p className={`text-sm font-semibold leading-snug ${estaHecha(tarea) ? "text-gray-500 line-through" : ""}`}>{tarea.titulo}</p>
        <Avatar nombre={responsable} tamano="h-7 w-7 text-[9px]" />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Etiqueta clase={prioridad.clase}>{prioridad.label}</Etiqueta>
        <span className={`text-[11px] font-semibold ${vence.clase}`}>{vence.texto}</span>
        <span className="ml-auto flex gap-2 text-[11px] text-gray-500">
          {tarea.adjuntos > 0 && <span title="Documentos">📎 {tarea.adjuntos}</span>}
          {tarea.comentarios > 0 && <span title="Comentarios">💬 {tarea.comentarios}</span>}
        </span>
      </div>
    </>
  );
}

function TarjetaTarea({ tarea, movible, arrastrando, onAbrir, onEmpezarArrastre, onMover }) {
  return (
    <div
      role="button"
      tabIndex={0}
      onPointerDown={(e) => movible && onEmpezarArrastre(e, tarea)}
      onClick={() => onAbrir(tarea)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onAbrir(tarea); } }}
      className={`select-none rounded-xl border border-white/10 bg-zinc-900 p-3 transition hover:border-white/30 ${movible ? "md:cursor-grab" : "cursor-pointer"} ${arrastrando ? "opacity-30" : ""}`}
    >
      <ContenidoTarjeta tarea={tarea} />
      {/* En el celular no se arrastra (el dedo desplaza la página): se cambia el estado aquí. */}
      <Selector
        aria-label={`Estado de ${tarea.titulo}`}
        value={tarea.estado}
        disabled={!movible}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        onChange={(e) => onMover(tarea, e.target.value)}
        className="mt-3 w-full rounded-lg border border-white/10 bg-zinc-800 px-2 py-1.5 text-xs md:hidden"
      >
        {ESTADOS_TAREA.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
      </Selector>
    </div>
  );
}

// Distancia (px) que hay que mover el mouse para que un clic se vuelva arrastre.
const UMBRAL_ARRASTRE = 6;
const MARGEN_DESPLAZAMIENTO = 80;

// Arrastre con eventos de puntero (mouse y lápiz) en lugar del arrastre nativo de HTML5,
// que se cancela con facilidad y no muestra bien dónde va a caer la tarjeta.
function useArrastreDeTareas(onMover) {
  const [arrastre, setArrastre] = useState(null); // { tarea, x, y, dx, dy, ancho }
  const [sobre, setSobre] = useState(null);
  const inicio = useRef(null);
  const recienArrastrada = useRef(false);

  useEffect(() => {
    const columnaEn = (x, y) => document.elementFromPoint(x, y)?.closest("[data-columna]")?.dataset.columna || null;

    const mover = (e) => {
      const datos = inicio.current;
      if (!datos || e.pointerId !== datos.pointerId) return;
      if (!datos.activo) {
        if (Math.hypot(e.clientX - datos.x, e.clientY - datos.y) < UMBRAL_ARRASTRE) return;
        datos.activo = true;
      }
      e.preventDefault();
      setArrastre({ tarea: datos.tarea, x: e.clientX, y: e.clientY, dx: datos.dx, dy: datos.dy, ancho: datos.ancho });
      setSobre(columnaEn(e.clientX, e.clientY));
      if (e.clientY < MARGEN_DESPLAZAMIENTO) window.scrollBy(0, -12);
      else if (e.clientY > window.innerHeight - MARGEN_DESPLAZAMIENTO) window.scrollBy(0, 12);
    };

    const soltar = (e) => {
      const datos = inicio.current;
      if (!datos || e.pointerId !== datos.pointerId) return;
      inicio.current = null;
      if (!datos.activo) return; // Fue un clic: lo maneja onClick.
      recienArrastrada.current = true; // El clic que sigue al soltar no abre la tarea.
      setTimeout(() => { recienArrastrada.current = false; }, 0);
      const estado = columnaEn(e.clientX, e.clientY);
      setArrastre(null);
      setSobre(null);
      if (estado && estado !== datos.tarea.estado) onMover(datos.tarea, estado);
    };

    const cancelar = (e) => {
      if (e.type === "keydown" && e.key !== "Escape") return;
      inicio.current = null;
      setArrastre(null);
      setSobre(null);
    };

    window.addEventListener("pointermove", mover);
    window.addEventListener("pointerup", soltar);
    window.addEventListener("pointercancel", cancelar);
    window.addEventListener("keydown", cancelar);
    return () => {
      window.removeEventListener("pointermove", mover);
      window.removeEventListener("pointerup", soltar);
      window.removeEventListener("pointercancel", cancelar);
      window.removeEventListener("keydown", cancelar);
    };
  }, [onMover]);

  const empezar = (e, tarea) => {
    // Solo botón principal del mouse o lápiz; en pantallas táctiles el dedo desplaza la página.
    if (e.button !== 0 || e.pointerType === "touch" || e.target.closest("select, button, a, input")) return;
    const caja = e.currentTarget.getBoundingClientRect();
    inicio.current = {
      tarea, pointerId: e.pointerId, x: e.clientX, y: e.clientY,
      dx: e.clientX - caja.left, dy: e.clientY - caja.top, ancho: caja.width, activo: false,
    };
  };

  return { arrastre, sobre, empezar, recienArrastrada };
}

function Tablero({ tareas, puedeMover, puedeCrear, onAbrir, onNueva, onMover }) {
  const { arrastre, sobre, empezar, recienArrastrada } = useArrastreDeTareas(onMover);
  const abrir = (tarea) => { if (!recienArrastrada.current) onAbrir(tarea); };

  return (
    <div className={`grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4 ${arrastre ? "cursor-grabbing select-none" : ""}`}>
      {ESTADOS_TAREA.map((estado) => {
        const columna = tareas.filter((t) => t.estado === estado.id);
        const destino = arrastre && sobre === estado.id && arrastre.tarea.estado !== estado.id;
        return (
          <section
            key={estado.id}
            data-columna={estado.id}
            aria-label={estado.label}
            className={`min-h-[220px] rounded-2xl border p-3 transition-colors ${destino ? "border-white/50 bg-zinc-900" : "border-white/10 bg-zinc-950"}`}
          >
            <div className="mb-3 flex items-center justify-between px-1">
              <div className="flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${estado.punto}`} />
                <span className="text-xs font-black uppercase tracking-widest">{estado.label}</span>
                <span className="text-xs text-gray-500">{columna.length}</span>
              </div>
              {puedeCrear && (
                <button
                  type="button"
                  title="Nueva tarea"
                  aria-label={`Nueva tarea en ${estado.label}`}
                  onClick={() => onNueva({ estado: estado.id })}
                  className="h-7 w-7 rounded-full text-lg leading-none text-gray-400 transition hover:bg-white hover:text-black"
                >
                  +
                </button>
              )}
            </div>
            <div className="space-y-2">
              {columna.map((t) => (
                <TarjetaTarea
                  key={t.id}
                  tarea={t}
                  movible={puedeMover(t)}
                  arrastrando={arrastre?.tarea.id === t.id}
                  onAbrir={abrir}
                  onEmpezarArrastre={empezar}
                  onMover={onMover}
                />
              ))}
              {destino && <div className="h-16 rounded-xl border-2 border-dashed border-white/30" aria-hidden="true" />}
            </div>
          </section>
        );
      })}
      {arrastre && (
        <div
          aria-hidden="true"
          className="pointer-events-none fixed left-0 top-0 z-50 rounded-xl border border-white/40 bg-zinc-800 p-3 shadow-2xl"
          style={{ width: arrastre.ancho, transform: `translate(${arrastre.x - arrastre.dx}px, ${arrastre.y - arrastre.dy}px) rotate(2deg)` }}
        >
          <ContenidoTarjeta tarea={arrastre.tarea} />
        </div>
      )}
    </div>
  );
}

function Lista({ tareas, puedeMover, onAbrir, onMover }) {
  if (!tareas.length) return <div className={panel}><p className="p-10 text-center text-sm text-gray-500">Este proyecto aún no tiene tareas.</p></div>;
  return (
    <div className={`${panel} divide-y divide-white/5 overflow-hidden`}>
      {tareas.map((t) => {
        const vence = vencimiento(t);
        const estado = datosDe(ESTADOS_TAREA, t.estado);
        const prioridad = datosDe(PRIORIDADES, t.prioridad);
        const responsable = nombrePersona(t.responsable);
        return (
          <div
            key={t.id}
            role="button"
            tabIndex={0}
            onClick={() => onAbrir(t)}
            onKeyDown={(e) => { if (e.key === "Enter") onAbrir(t); }}
            className={`flex cursor-pointer flex-wrap items-center gap-3 px-5 py-3 md:flex-nowrap ${filaResaltable}`}
          >
            <button
              type="button"
              aria-label={estaHecha(t) ? `Reabrir ${t.titulo}` : `Marcar ${t.titulo} como hecha`}
              disabled={!puedeMover(t)}
              onClick={(e) => { e.stopPropagation(); onMover(t, estaHecha(t) ? "pendiente" : "completada"); }}
              className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 text-[10px] ${estaHecha(t) ? "border-emerald-400 bg-emerald-400 text-black" : "border-white/30 hover:border-emerald-400"}`}
            >
              {estaHecha(t) ? "✓" : ""}
            </button>
            <p className={`min-w-[10rem] flex-1 text-sm font-semibold ${estaHecha(t) ? "text-gray-500 line-through" : ""}`}>{t.titulo}</p>
            <Etiqueta clase={estado.clase}>{estado.label}</Etiqueta>
            <Etiqueta clase={prioridad.clase}>{prioridad.label}</Etiqueta>
            <span className={`w-36 text-right text-xs font-semibold ${vence.clase}`}>{vence.texto}</span>
            <div className="flex w-40 items-center gap-2">
              <Avatar nombre={responsable} tamano="h-7 w-7 text-[9px]" />
              <span className="truncate text-xs text-gray-300">{responsable || "Sin asignar"}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DetalleProyecto({ proyecto, tareas, personas, usuario, onVolver, onCambio, onMoverTarea, onEliminado }) {
  const [vista, setVista] = useState(leerVista);
  const [editando, setEditando] = useState(false);
  // null: cerrada; { tarea, inicial }: abierta (sin tarea es una nueva).
  const [ventanaTarea, setVentanaTarea] = useState(null);

  const propias = ordenarTareas(tareas.filter((t) => t.proyecto_id === proyecto.id));
  const hechas = propias.filter(estaHecha).length;
  const vencidas = propias.filter(estaVencida).length;
  const porcentaje = avance(propias.length, hechas, proyecto.estado);
  const esperado = avanceEsperado(proyecto);
  const idSalud = salud(proyecto, porcentaje, vencidas > 0);
  const estado = datosDe(ESTADOS_PROYECTO, proyecto.estado);
  const prioridad = datosDe(PRIORIDADES, proyecto.prioridad);
  const lider = nombrePersona(proyecto.lider);
  const administra = gestiona(usuario, proyecto);
  const puedeMover = (t) => administra || esResponsable(usuario, t);
  const titulos = Object.fromEntries(propias.map((t) => [t.id, t.titulo]));
  const conPortada = Boolean(proyecto.portada_url);

  const elegirVista = (id) => {
    setVista(id);
    try { localStorage.setItem(VISTA_GUARDADA, id); } catch { /* preferencia opcional */ }
  };
  const abrirTarea = (tarea) => setVentanaTarea({ tarea });
  const nuevaTarea = (inicial = {}) => setVentanaTarea({ tarea: null, inicial });

  const eliminar = async () => {
    const aviso = propias.length
      ? `¿Eliminar "${proyecto.nombre}" y sus ${propias.length} tareas, comentarios y documentos? Esta acción no se puede deshacer.`
      : `¿Eliminar "${proyecto.nombre}"? Esta acción no se puede deshacer.`;
    if (!(await confirmar(aviso, { aceptar: "Eliminar proyecto" }))) return;
    try {
      await eliminarProyecto(proyecto);
      onEliminado();
    } catch (error) {
      avisarError(error);
    }
  };

  return (
    <div className={contenedor}>
      <button type="button" onClick={onVolver} className="text-[10px] font-bold uppercase tracking-widest text-gray-500 hover:text-white">← Proyectos</button>

      <div className={`relative isolate mb-8 mt-4 flex flex-wrap items-end justify-between gap-6 ${conPortada ? "min-h-[320px] overflow-hidden rounded-2xl border border-white/10 p-6 sm:p-10" : ""}`}>
        {conPortada && (
          <div className="absolute inset-0 -z-10">
            <img src={proyecto.portada_url} alt="" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/20" />
          </div>
        )}
        <div className="max-w-3xl">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <Etiqueta clase={estado.clase}>{estado.label}</Etiqueta>
            {idSalud !== "cerrado" && <Etiqueta clase={SALUD[idSalud].clase}>{SALUD[idSalud].label}</Etiqueta>}
            <Etiqueta clase={prioridad.clase}>Prioridad {prioridad.label}</Etiqueta>
            {proyecto.ministerio && <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500">{proyecto.ministerio}</span>}
          </div>
          <h2 className={tituloPagina}>{proyecto.nombre}</h2>
          {proyecto.descripcion && (
            <p className={`mt-4 whitespace-pre-line font-light ${conPortada ? "text-gray-200" : "text-gray-400"}`}>{proyecto.descripcion}</p>
          )}
        </div>
        {administra && (
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => setEditando(true)} className={botonFantasma}>Editar</button>
            <button type="button" onClick={eliminar} className={botonPeligro}>Eliminar</button>
            <button type="button" onClick={() => nuevaTarea()} className={botonPrimario}>+ Tarea</button>
          </div>
        )}
      </div>

      <div className="mb-8 grid grid-cols-2 gap-4 md:grid-cols-4">
        <div className={`${panel} p-5`}>
          <p className={etiqueta}>Líder</p>
          <div className="flex items-center gap-2">
            <Avatar nombre={lider} />
            <span className="truncate text-sm font-semibold">{lider || "Sin líder"}</span>
          </div>
        </div>
        <div className={`${panel} p-5`}>
          <p className={etiqueta}>Fechas</p>
          <p className="text-sm font-semibold">{fechaCorta(proyecto.fecha_inicio)} – {fechaCorta(proyecto.fecha_fin)}</p>
        </div>
        <div className={`${panel} p-5`}>
          <p className={etiqueta}>Avance</p>
          <div className="flex items-center gap-3">
            <span className="text-2xl font-black">{porcentaje}%</span>
            <Barra porcentaje={porcentaje} className="flex-1" />
          </div>
          {esperado !== null && <p className="mt-1 text-[11px] text-gray-500">Esperado hoy: {Math.round(esperado)}%</p>}
        </div>
        <div className={`${panel} p-5`}>
          <p className={etiqueta}>Tareas</p>
          <p className="text-sm font-semibold">{hechas} hechas · {propias.length - hechas} abiertas</p>
          <p className={`mt-1 text-[11px] ${vencidas ? "text-rose-400" : "text-gray-500"}`}>{vencidas} vencidas</p>
        </div>
      </div>

      <div className="mb-6 flex gap-1 overflow-x-auto border-b border-white/10 pb-3">
        {VISTAS.map((v) => (
          <button key={v.id} type="button" onClick={() => elegirVista(v.id)} className={pestana(vista === v.id)}>{v.label}</button>
        ))}
      </div>

      {vista === "tablero" && (
        <Tablero tareas={propias} puedeMover={puedeMover} puedeCrear={administra} onAbrir={abrirTarea} onNueva={nuevaTarea} onMover={onMoverTarea} />
      )}
      {vista === "lista" && <Lista tareas={propias} puedeMover={puedeMover} onAbrir={abrirTarea} onMover={onMoverTarea} />}
      {vista === "documentos" && (
        <Documentos
          destino={{ proyecto_id: proyecto.id }}
          puedeSubir={administra}
          puedeBorrar={(a) => administra || (usuario?.miembro_id != null && a.subido_por_id === usuario.miembro_id)}
          tituloTarea={(id) => titulos[id] || ""}
          onAbrirTarea={(id) => { const t = propias.find((x) => x.id === id); if (t) abrirTarea(t); }}
          onCambio={onCambio}
        />
      )}

      {editando && (
        <FormProyecto
          proyecto={proyecto}
          personas={personas}
          puedeCambiarLider={esAdministrador(usuario)}
          onGuardado={() => { setEditando(false); onCambio(); }}
          onCerrar={() => setEditando(false)}
        />
      )}
      {ventanaTarea && (
        <FormTarea
          key={ventanaTarea.tarea?.id || "nueva"}
          tarea={ventanaTarea.tarea}
          inicial={ventanaTarea.inicial}
          proyecto={proyecto}
          personas={personas}
          usuario={usuario}
          onCambio={onCambio}
          onCerrar={() => setVentanaTarea(null)}
        />
      )}
    </div>
  );
}

export default DetalleProyecto;
